<template>
  <section class="ncr-activity-pane">
    <div v-if="entries.length === 0" class="ncr-pane-empty">No activity recorded yet.</div>
    <ol v-else class="ncr-audit-list">
      <li v-for="entry in entries" :key="entry.id">
        <span class="ncr-audit-marker"></span>
        <div>
          <header>
            <strong>{{ entry.user || 'System' }}</strong>
            <time>{{ formatDate(entry.timestamp) }}</time>
          </header>
          <p v-if="entry.type === 'create'">{{ displayValue(entry.newValue) }} was created.</p>
          <p v-else-if="entry.field === 'files'">Modified an attachment.</p>
          <p v-else>
            Changed <strong>{{ entry.field || 'record' }}</strong>
            from <span>{{ displayValue(entry.oldValue) }}</span>
            to <span>{{ displayValue(entry.newValue) }}</span>.
          </p>
        </div>
      </li>
    </ol>
  </section>
</template>

<script setup lang="ts">
import { onBeforeUnmount, ref, watch } from 'vue';
import type { Unsubscribe } from 'firebase/firestore';

import { ncrClient, type NcrAuditEntry } from '@/modules/ncr/NcrClient';

const props = defineProps<{ recordId: string }>();
const entries = ref<NcrAuditEntry[]>([]);
let unsubscribe: Unsubscribe | null = null;

function subscribe(recordId: string): void {
  unsubscribe?.();
  unsubscribe = null;
  entries.value = [];
  if (!recordId) return;
  unsubscribe = ncrClient.subscribeToAudit(recordId, nextEntries => {
    entries.value = nextEntries;
  });
}

function displayValue(value: unknown): string {
  if (value === '' || value === null || value === undefined) return 'blank';
  if (Array.isArray(value)) return value.length ? `${value.length} item(s)` : 'blank';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function formatDate(value: any): string {
  if (typeof value?.toMillis === 'function') return new Date(value.toMillis()).toLocaleString();
  if (typeof value?.seconds === 'number') return new Date(value.seconds * 1000).toLocaleString();
  const numeric = Number(value || 0);
  return numeric ? new Date(numeric > 1_000_000_000_000 ? numeric : numeric * 1000).toLocaleString() : '';
}

watch(() => props.recordId, subscribe, { immediate: true });
onBeforeUnmount(() => unsubscribe?.());
</script>
